import { Button, Chip, Field, Panel, Switch } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type ContactRow, type TreeNode, type TreeUnit } from "../api.js";
import { askReason, ErrorLine, useAction } from "./common.js";

/** Organizations, units and contacts (design/v2 Organizations.html; menus and prices arrive with F3). */
export function OrganizationsPage() {
  const tree = useQuery({ queryKey: ["organizations"], queryFn: () => api<TreeNode[]>("/organizations") });
  const [unit, setUnit] = useState<(TreeUnit & { org: string }) | null>(null);
  const [orgName, setOrgName] = useState("");
  const [parentId, setParentId] = useState("");
  const [withUnit, setWithUnit] = useState(true);
  const [unitName, setUnitName] = useState("");
  const [unitOrg, setUnitOrg] = useState("");

  const all: TreeNode[] = [];
  const walk = (ns: TreeNode[]) => {
    for (const n of ns) {
      all.push(n);
      walk(n.children);
    }
  };
  walk(tree.data ?? []);

  const addOrg = useAction(async () => {
    await api("/organizations", {
      body: { parentId: parentId || null, name: orgName, withDefaultUnit: withUnit },
    });
    setOrgName("");
  }, [["organizations"]]);
  const addUnit = useAction(async () => {
    await api("/units", { body: { organizationId: unitOrg, name: unitName } });
    setUnitName("");
  }, [["organizations"]]);

  const Node = ({ n, depth }: { n: TreeNode; depth: number }) => (
    <div style={{ paddingInlineStart: depth * 20 }}>
      <div className="row-between tree-row">
        <span className="m">{n.name}</span>
        {n.code && <Chip plain>{n.code}</Chip>}
      </div>
      {n.units.map((u) => (
        <button
          key={u.id}
          type="button"
          className={`tree-unit${unit?.id === u.id ? " is-active" : ""}`}
          style={{ marginInlineStart: 20 }}
          onClick={() => {
            setUnit({ ...u, org: n.name });
          }}
        >
          <span>{u.name}</span>
          <span className="t-cap muted">{u.contacts ? `${String(u.contacts)} رابط` : "بدون رابط"}</span>
        </button>
      ))}
      {n.children.map((c) => (
        <Node key={c.id} n={c} depth={depth + 1} />
      ))}
    </div>
  );

  return (
    <div className="grid" style={{ gridTemplateColumns: "380px minmax(0,1fr)", alignItems: "start" }}>
      <Panel title="سازمان‌ها">
        <div className="stack" style={{ gap: 2 }}>
          {(tree.data ?? []).map((n) => (
            <Node key={n.id} n={n} depth={0} />
          ))}
          {tree.data?.length === 0 && <p className="muted">هنوز سازمانی ثبت نشده است.</p>}
        </div>
      </Panel>
      <div className="stack">
        {unit && <UnitContacts unit={unit} />}
        <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", alignItems: "start" }}>
          <Panel title="سازمان جدید">
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                addOrg.run(undefined);
              }}
            >
              <Field
                id="org-name"
                label="نام"
                value={orgName}
                onChange={(e) => {
                  setOrgName(e.target.value);
                }}
              />
              <div className="field">
                <label htmlFor="org-parent">زیرمجموعه‌ی</label>
                <select
                  id="org-parent"
                  className="input"
                  value={parentId}
                  onChange={(e) => {
                    setParentId(e.target.value);
                  }}
                >
                  <option value="">هیچ (سازمان اصلی)</option>
                  {all.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
              <Switch
                label="یک واحد سفارش‌دهنده به همین نام هم بساز"
                checked={withUnit}
                onChange={setWithUnit}
              />
              <Button type="submit" variant="primary" icon="plus" disabled={!orgName || addOrg.busy}>
                افزودن سازمان
              </Button>
              <ErrorLine error={addOrg.error} />
            </form>
          </Panel>
          <Panel title="واحد سفارش‌دهنده‌ی جدید">
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                addUnit.run(undefined);
              }}
            >
              <Field
                id="unit-name"
                label="نام واحد"
                value={unitName}
                onChange={(e) => {
                  setUnitName(e.target.value);
                }}
              />
              <div className="field">
                <label htmlFor="unit-org">سازمان</label>
                <select
                  id="unit-org"
                  className="input"
                  value={unitOrg}
                  onChange={(e) => {
                    setUnitOrg(e.target.value);
                  }}
                >
                  <option value="">انتخاب کنید</option>
                  {all.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                type="submit"
                variant="primary"
                icon="plus"
                disabled={!unitName || !unitOrg || addUnit.busy}
              >
                افزودن واحد
              </Button>
              <ErrorLine error={addUnit.error} />
            </form>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function UnitContacts({ unit }: { unit: TreeUnit & { org: string } }) {
  const contacts = useQuery({
    queryKey: ["contacts", unit.id],
    queryFn: () => api<ContactRow[]>(`/units/${unit.id}/contacts`),
  });
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const add = useAction(async () => {
    await api(`/units/${unit.id}/contacts`, {
      body: { name, phone, isPrimary: (contacts.data ?? []).length === 0 },
    });
    setName("");
    setPhone("");
  }, [["contacts", unit.id], ["organizations"]]);
  const archive = useAction(
    async (c: ContactRow) => {
      const reason = askReason(c.name);
      if (reason) await api(`/contacts/${c.id}/archive`, { body: { version: c.version, reason } });
    },
    [["contacts", unit.id], ["organizations"]],
  );

  return (
    <Panel title={`رابط‌های ${unit.org}، ${unit.name}`}>
      <div className="card" style={{ padding: "var(--s2) var(--s4)" }}>
        <table className="tbl">
          <tbody>
            {(contacts.data ?? []).map((c) => (
              <tr key={c.id}>
                <td className="m">{c.name}</td>
                <td className="num" dir="ltr" style={{ textAlign: "start" }}>
                  {`0${c.phone.slice(3)}`}
                </td>
                <td>{c.linked ? <Chip tone="ok">وارد شده</Chip> : <Chip plain>هنوز وارد نشده</Chip>}</td>
                <td className="end">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      archive.run(c);
                    }}
                  >
                    بایگانی
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form
        className="row wrap"
        style={{ marginTop: "var(--s4)", alignItems: "flex-end" }}
        onSubmit={(e) => {
          e.preventDefault();
          add.run(undefined);
        }}
      >
        <Field
          id="c-name"
          label="نام رابط"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
        <Field
          id="c-phone"
          label="موبایل"
          dir="ltr"
          inputMode="tel"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
          }}
        />
        <Button type="submit" icon="plus" disabled={!name || !phone || add.busy}>
          افزودن رابط
        </Button>
      </form>
      <p className="hint">رابط با همین شماره از «ورود مشتری» وارد می‌شود و مستقیم به این واحد می‌رسد.</p>
      <ErrorLine error={add.error ?? archive.error} />
    </Panel>
  );
}
